import {
  Mail, Send, Edit, CheckCircle2, X, AlertCircle, CalendarClock, Timer, FlaskConical, Flame,
} from 'lucide-react';

// Built-in quick-reply templates. {name} is substituted with the lead's first name.
export const QUICK_TEMPLATES = [
  { label: 'Thanks for the reply', body: 'Hi {name},\n\nThanks for getting back to me — really appreciate it. ' },
  { label: 'Propose a call', body: 'Hi {name},\n\nWould you be open to a quick 15-minute call this week to walk through the details? Here are a couple of times that work on my end — happy to adjust to yours:\n\n• \n• \n\nLet me know what suits you best.' },
  { label: 'Share pricing', body: 'Hi {name},\n\nHappy to share pricing. Based on what you described, here is what I would recommend:\n\n• \n\nLet me know if you would like me to put together a formal quote.' },
  { label: 'Gentle follow-up', body: 'Hi {name},\n\nJust following up on my note below — did you get a chance to take a look? Happy to answer any questions.' },
  { label: 'Answer a question', body: 'Hi {name},\n\nGreat question. ' },
];

export const STATUS_STYLES = {
  draft: { label: 'Draft', color: 'bg-muted text-muted-foreground border-border', dot: 'bg-amber-500', icon: Edit },
  approved: { label: 'Approved', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', dot: 'bg-emerald-500', icon: CheckCircle2 },
  scheduled: { label: 'Scheduled', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20', dot: 'bg-purple-500', icon: CalendarClock },
  sent: { label: 'Sent', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20', dot: 'bg-blue-500', icon: Send },
  simulated: { label: 'Simulated', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', dot: 'bg-amber-500', icon: FlaskConical },
  needs_mailbox: { label: 'Awaiting Mailbox', color: 'bg-orange-500/10 text-orange-600 border-orange-500/20', dot: 'bg-orange-500', icon: AlertCircle },
  capped: { label: 'Daily Cap', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', dot: 'bg-amber-500', icon: Timer },
  send_failed: { label: 'Send Failed', color: 'bg-red-500/10 text-red-600 border-red-500/20', dot: 'bg-red-500', icon: AlertCircle },
  dismissed: { label: 'Dismissed', color: 'bg-red-500/10 text-red-500 border-red-500/20', dot: 'bg-red-500', icon: X },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground border-border', dot: 'bg-zinc-500', icon: X },
};

export const titleCase = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
export const getStatusStyle = (status) => STATUS_STYLES[status] || { label: titleCase(status) || 'Unknown', color: 'bg-muted text-muted-foreground border-border', dot: 'bg-zinc-500', icon: Mail };

export const MODE_BADGES = {
  smtp: { label: 'SMTP', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  sendgrid: { label: 'SendGrid', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  free_pool: { label: 'Lockene Pool', color: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20' },
  demo: { label: 'Demo', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
};

export const ENGAGEMENT_STYLES = {
  hot: { label: 'Hot', color: 'bg-red-500/15 text-red-600 border-red-500/30', icon: Flame },
  warm: { label: 'Warm', color: 'bg-orange-500/15 text-orange-600 border-orange-500/30', icon: null },
  cold: { label: 'Cold', color: 'bg-muted text-muted-foreground border-border', icon: null },
};

export const OUTGOING_TABS = [
  { key: 'draft', label: 'Drafts' },
  { key: 'approved', label: 'Approved' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'sent', label: 'Sent' },
  { key: 'all', label: 'All' },
];

export function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (now.getFullYear() === d.getFullYear()) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

export function snippetOf(text, len = 90) {
  return (text || '').replace(/\s+/g, ' ').trim().slice(0, len);
}

export function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');
}

// Gmail-style date grouping → ordered [{label, items}]
export function groupByDate(items, getDate) {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(now);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const weekStart = new Date(today); weekStart.setDate(today.getDate() - 6);
  const groups = new Map();
  const order = [];
  const push = (key, item) => { if (!groups.has(key)) { groups.set(key, []); order.push(key); } groups.get(key).push(item); };
  const sorted = [...items].sort((a, b) => new Date(getDate(b) || 0) - new Date(getDate(a) || 0));
  for (const it of sorted) {
    const raw = getDate(it);
    const d = raw ? new Date(raw) : null;
    let key;
    if (!d || isNaN(d.getTime())) key = 'Earlier';
    else {
      const sd = startOfDay(d);
      if (sd.getTime() === today.getTime()) key = 'Today';
      else if (sd.getTime() === yesterday.getTime()) key = 'Yesterday';
      else if (sd >= weekStart) key = 'Earlier this week';
      else key = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
    push(key, it);
  }
  return order.map(k => ({ label: k, items: groups.get(k) }));
}

export const draftSortDate = (d) => (d.status === 'sent' && d.sent_at) ? d.sent_at : (d.created_at || d.scheduled_send_at);

// Absolute "Mon D, h:mm AM" label for the time an item was actually sent (matches LinkedIn Outbox).
export function sentAtLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
